import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, Video, Star, CheckCircle2, Award, Shield, ChevronRight, PlayCircle, MessageSquare, Heart, Share2, BookOpen, ArrowRight, Users } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Rating } from '../components/ui/Rating';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface ProductDetailPageProps {
  onNavigate: (page: string) => void;
}
export function ProductDetailPage({
  onNavigate
}: ProductDetailPageProps) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const {
    addItem
  } = useCart();
  const handleBuyProduct = () => {
    addItem({
      id: `course-${Date.now()}`,
      name: 'Основы ведической астрологии: с нуля до первых прогнозов',
      description: 'Научитесь читать свою натальную карту за 4 недели. Практические задания, обратная связь от кураторов и сертификат по окончании.',
      price: 15000,
      oldPrice: 18000,
      type: 'course',
      astrologer: {
        name: 'Михаил Волков',
        initials: 'МВ',
        rating: 5.0,
        reviews: 89
      },
      format: 'Онлайн-курс',
      duration: '4 недели',
      badges: [{
        label: 'Онлайн-курс',
        variant: 'pro'
      }]
    });
    onNavigate('checkout');
  };
  const handleShare = () => {
    navigator.clipboard?.writeText(window.location.href);
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 2000);
  };
  const discountPercent = Math.round((1 - 15000 / 18000) * 100);
  const curriculum = [{
    id: '1',
    title: 'Модуль 1: Основы и стихии',
    content: 'Знакомство с астрологией. 4 стихии, кресты качеств. Как стихии проявляются в характере человека. Практика: определение ведущей стихии.'
  }, {
    id: '2',
    title: 'Модуль 2: Знаки зодиака',
    content: 'Подробный разбор каждого из 12 знаков. Светлая и теневая сторона. Мотивация знаков. Практика: анализ Солнца и Луны в знаках.'
  }, {
    id: '3',
    title: 'Модуль 3: Планеты',
    content: 'Личные, социальные и высшие планеты. Функции планет в психике. Ретроградность. Практика: разбор личных планет в карте.'
  }, {
    id: '4',
    title: 'Модуль 4: Дома гороскопа',
    content: 'Сферы жизни. Угловые, последующие и падающие дома. Управители домов. Практика: синтез планета + знак + дом.'
  }];
  const relatedServices = [{
    id: 1,
    title: 'Консультация: Предназначение и карьера',
    astrologer: 'Анна Коваль',
    initials: 'АК',
    avatarColor: 'bg-amber-100 text-amber-700',
    price: '5 500 ₽',
    rating: 4.9,
    reviews: 67,
    format: 'Видеозвонок',
    duration: '90 мин'
  }, {
    id: 2,
    title: 'Марафон: Проработка Венеры',
    astrologer: 'Михаил Волков',
    initials: 'МВ',
    avatarColor: 'bg-emerald-100 text-emerald-700',
    price: '5 000 ₽',
    rating: 4.8,
    reviews: 312,
    format: 'Telegram',
    duration: '14 дней'
  }, {
    id: 3,
    title: 'Письменный разбор: Синастрия',
    astrologer: 'Дмитрий Орлов',
    initials: 'ДО',
    avatarColor: 'bg-rose-100 text-rose-700',
    price: '3 000 ₽',
    rating: 4.9,
    reviews: 128,
    format: 'PDF-файл',
    duration: '3-4 дня'
  }, {
    id: 4,
    title: 'Вебинар: Лунные циклы и планирование',
    astrologer: 'София Романова',
    initials: 'СР',
    avatarColor: 'bg-blue-100 text-blue-700',
    price: '1 200 ₽',
    rating: 4.6,
    reviews: 34,
    format: 'Запись + PDF',
    duration: '1.5 часа'
  }];
  return <div className="w-full bg-slate-50 min-h-screen pb-32 lg:pb-24" data-id="element-26007">
      {/* Hero Section */}
      <div className="bg-slate-900 text-white pt-8 pb-16" data-id="element-26008">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26009">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Маркетплейс',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Курсы и марафоны',
          onClick: () => onNavigate('marketplace')
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-26010" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-26011">
            <div data-id="element-26012">
              <div className="flex items-center gap-3 mb-6" data-id="element-26013">
                <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30" data-id="element-26014">
                  Онлайн-курс
                </Badge>
                <Badge variant="success" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-id="element-26015">
                  −{discountPercent}%
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-26016">
                Основы ведической астрологии: с нуля до первых прогнозов
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-26017">
                Научитесь читать свою натальную карту за 4 недели. Практические
                задания, обратная связь от кураторов и сертификат по окончании.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-slate-300 mb-6" data-id="element-26018">
                <div className="flex items-center" data-id="element-26019">
                  <Rating value={5.0} showCount={false} size="sm" data-id="element-26020" />
                  <span className="ml-2 font-medium text-white" data-id="element-26021">5.0</span>
                  <span className="ml-1 text-slate-400" data-id="element-26022">(89 отзывов)</span>
                </div>
                <div className="flex items-center" data-id="element-26023">
                  <Clock className="w-4 h-4 mr-2 text-slate-400" data-id="element-26024" />4 недели
                </div>
                <div className="flex items-center" data-id="element-26025">
                  <Video className="w-4 h-4 mr-2 text-slate-400" data-id="element-26026" />
                  24 видеоурока
                </div>
                <div className="flex items-center" data-id="element-26027">
                  <Users className="w-4 h-4 mr-2 text-slate-400" data-id="element-26028" />
                  340+ учеников
                </div>
              </div>

              {/* Share & Favorite */}
              <div className="flex items-center gap-3" data-id="element-26029">
                <button onClick={() => setIsFavorite(!isFavorite)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${isFavorite ? 'bg-rose-500/20 border-rose-500/40 text-rose-300' : 'bg-white/5 border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`} aria-label={isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'} data-id="element-26030">
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-400' : ''}`} data-id="element-26031" />
                  {isFavorite ? 'В избранном' : 'В избранное'}
                </button>
                <button onClick={handleShare} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-700 bg-white/5 text-slate-400 hover:text-white hover:border-slate-500 text-sm font-medium transition-all" aria-label="Поделиться" data-id="element-26032">
                  <Share2 className="w-4 h-4" data-id="element-26033" />
                  Поделиться
                </button>
                {showShareToast && <span className="text-xs text-emerald-400 animate-pulse" data-id="element-26034">
                    Ссылка скопирована!
                  </span>}
              </div>
            </div>

            <div className="relative" data-id="element-26035">
              <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 shadow-2xl relative group cursor-pointer flex items-center justify-center" role="button" aria-label="Смотреть превью курса" tabIndex={0} data-id="element-26036">
                <div className="absolute inset-0 bg-gradient-to-tr from-indigo-900/40 to-slate-800/40 mix-blend-overlay" data-id="element-26037"></div>
                <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center group-hover:bg-primary transition-colors z-10" data-id="element-26038">
                  <PlayCircle className="w-8 h-8 text-white ml-1" data-id="element-26039" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-26040">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-26041">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-12" data-id="element-26042">
            {/* About */}
            <section data-id="element-26043">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-26044">
                О курсе
              </h2>
              <div className="prose prose-slate max-w-none text-slate-600" data-id="element-26045">
                <p data-id="element-26046">
                  Этот курс создан специально для тех, кто хочет изучать
                  астрологию "для себя", но готов погрузиться глубоко. Мы не
                  будем заучивать сухие трактовки из интернета. Вы научитесь
                  понимать логику астрологии, синтезировать показатели и видеть
                  за символами живого человека.
                </p>
                <p data-id="element-26047">
                  Уже после первой недели обучения вы сможете построить свою
                  карту и понять свои базовые потребности, таланты и точки
                  напряжения.
                </p>
              </div>
            </section>

            {/* What you'll learn */}
            <section data-id="element-26048">
              <Card padding="lg" className="bg-indigo-50/50 border-indigo-100" data-id="element-26049">
                <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-26050">
                  Чему вы научитесь:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-26051">
                  {['Строить натальную карту в профессиональной программе', 'Определять сильные и слабые стороны личности', 'Видеть финансовый потенциал и подходящие профессии', 'Понимать кармические задачи (Лунные узлы)', 'Анализировать сферу отношений и брака', 'Подбирать компенсаторные действия для сложных аспектов'].map((item, i) => <div key={i} className="flex items-start" data-id="element-26052">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-26053" />
                      <span className="text-sm text-slate-700 leading-relaxed" data-id="element-26054">
                        {item}
                      </span>
                    </div>)}
                </div>
              </Card>
            </section>

            {/* Curriculum */}
            <section data-id="element-26055">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-26056">
                Программа обучения
              </h2>
              <Accordion items={curriculum} data-id="element-26057" />
            </section>

            {/* Reviews */}
            <section data-id="element-26058">
              <div className="flex items-center justify-between mb-8" data-id="element-26059">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-26060">
                  Отзывы учеников
                </h2>
                <div className="flex items-center" data-id="element-26061">
                  <Rating value={5.0} showCount={false} data-id="element-26062" />
                  <span className="ml-2 font-bold text-slate-900" data-id="element-26063">5.0</span>
                </div>
              </div>

              <div className="space-y-6" data-id="element-26064">
                {[{
                name: 'Ольга С.',
                initial: 'О',
                batch: 'Выпускница 3 потока',
                text: 'Потрясающий курс! Михаил объясняет сложные вещи очень простым языком. Я пыталась изучать астрологию сама по книгам, но в голове была каша. Здесь же всё разложили по полочкам. Особенно ценно, что мы практиковались на своих картах.'
              }, {
                name: 'Мария В.',
                initial: 'М',
                batch: 'Выпускница 4 потока',
                text: 'Лучшая инвестиция в себя за последний год. Я наконец-то поняла, почему у меня постоянно возникали одни и те же проблемы в отношениях. Кураторы отвечали на вопросы очень подробно. Рекомендую всем!'
              }, {
                name: 'Екатерина Д.',
                initial: 'Е',
                batch: 'Выпускница 5 потока',
                text: 'Очень структурированная подача материала. Каждый модуль логично вытекает из предыдущего. После курса я уже делаю разборы для подруг и они в восторге. Спасибо Михаилу за его подход!'
              }].map((review, i) => <Card key={i} padding="lg" data-id="element-26065">
                    <div className="flex justify-between items-start mb-4" data-id="element-26066">
                      <div className="flex items-center gap-3" data-id="element-26067">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500" data-id="element-26068">
                          {review.initial}
                        </div>
                        <div data-id="element-26069">
                          <div className="font-bold text-slate-900" data-id="element-26070">
                            {review.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-26071">
                            {review.batch}
                          </div>
                        </div>
                      </div>
                      <Rating value={5} showCount={false} size="sm" data-id="element-26072" />
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-26073">
                      {review.text}
                    </p>
                  </Card>)}
              </div>
              <Button variant="outline" fullWidth className="mt-6" data-id="element-26074">
                Показать все отзывы (89)
              </Button>
            </section>

            {/* Related Services */}
            <section data-id="element-26075">
              <div className="flex items-center justify-between mb-6" data-id="element-26076">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-26077">
                  Похожие товары
                </h2>
                <button onClick={() => onNavigate('marketplace')} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center" data-id="element-26078">
                  Все услуги <ArrowRight className="w-4 h-4 ml-1" data-id="element-26079" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-26080">
                {relatedServices.map(service => <div key={service.id} className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer group hover:shadow-md transition-shadow" onClick={() => onNavigate('product-detail')} data-id="element-26081">
                    <div className="flex items-center gap-2.5 mb-3" data-id="element-26082">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${service.avatarColor}`} data-id="element-26083">
                        {service.initials}
                      </div>
                      <div className="min-w-0" data-id="element-26084">
                        <div className="text-xs font-medium text-slate-500 truncate" data-id="element-26085">
                          {service.astrologer}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-26086">
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-26087" />
                          <span className="text-xs font-medium text-slate-700" data-id="element-26088">
                            {service.rating}
                          </span>
                          <span className="text-[10px] text-slate-400" data-id="element-26089">
                            ({service.reviews})
                          </span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors leading-snug" data-id="element-26090">
                      {service.title}
                    </h3>
                    <div className="flex items-center gap-3 mb-3 text-xs text-slate-500" data-id="element-26091">
                      <span className="flex items-center" data-id="element-26092">
                        <BookOpen className="w-3 h-3 mr-1 text-slate-400" data-id="element-26093" />{' '}
                        {service.format}
                      </span>
                      <span className="flex items-center" data-id="element-26094">
                        <Clock className="w-3 h-3 mr-1 text-slate-400" data-id="element-26095" />{' '}
                        {service.duration}
                      </span>
                    </div>
                    <div className="text-base font-bold text-slate-900" data-id="element-26096">
                      {service.price}
                    </div>
                  </div>)}
              </div>
            </section>
          </div>

          {/* Right Column: Sticky Buy Box & Astrologer Info */}
          <div className="lg:col-span-4" data-id="element-26097">
            <div className="sticky top-24 space-y-6" data-id="element-26098">
              {/* Buy Box */}
              <Card padding="lg" className="border-primary/20 shadow-xl" data-id="element-26099">
                <div className="mb-6" data-id="element-26100">
                  <div className="flex items-center gap-3 mb-1" data-id="element-26101">
                    <span className="text-sm text-slate-500 line-through" data-id="element-26102">
                      18 000 ₽
                    </span>
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full" data-id="element-26103">
                      −{discountPercent}%
                    </span>
                  </div>
                  <div className="text-4xl font-bold text-slate-900" data-id="element-26104">
                    15 000 ₽
                  </div>
                </div>

                <div className="space-y-4 mb-8" data-id="element-26105">
                  <div className="flex items-center text-sm text-slate-600" data-id="element-26106">
                    <Video className="w-5 h-5 mr-3 text-slate-400" data-id="element-26107" />
                    Доступ к 24 видеоурокам навсегда
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-26108">
                    <MessageSquare className="w-5 h-5 mr-3 text-slate-400" data-id="element-26109" />
                    Чат с кураторами и автором
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-26110">
                    <Award className="w-5 h-5 mr-3 text-slate-400" data-id="element-26111" />
                    Сертификат по окончании
                  </div>
                  <div className="flex items-center text-sm text-slate-600" data-id="element-26112">
                    <Users className="w-5 h-5 mr-3 text-slate-400" data-id="element-26113" />
                    340+ учеников уже прошли
                  </div>
                </div>

                <Button size="lg" fullWidth onClick={handleBuyProduct} className="mb-4 shadow-md shadow-primary/20" data-id="element-26114">
                  Записаться на курс
                </Button>

                <div className="flex items-center justify-center text-xs text-slate-500" data-id="element-26115">
                  <Shield className="w-4 h-4 mr-1.5 text-emerald-500" data-id="element-26116" />
                  Безопасная оплата. Гарантия возврата 14 дней.
                </div>
              </Card>

              {/* Astrologer Card */}
              <Card padding="md" hoverable onClick={() => onNavigate('astrologer-profile')} data-id="element-26117">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-26118">
                  Автор курса
                </h3>
                <div className="flex items-center gap-4 mb-4" data-id="element-26119">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center text-xl font-bold text-emerald-700 flex-shrink-0" data-id="element-26120">
                    МВ
                  </div>
                  <div data-id="element-26121">
                    <div className="font-bold text-slate-900" data-id="element-26122">
                      Михаил Волков
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-26123">
                      Ведическая астрология
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4 line-clamp-3" data-id="element-26124">
                  Практикующий астролог с опытом более 10 лет. Основатель школы
                  ведической астрологии. Более 5000 проведенных консультаций.
                </p>
                <div className="flex items-center text-sm font-medium text-primary" data-id="element-26125">
                  Перейти в профиль <ChevronRight className="w-4 h-4 ml-1" data-id="element-26126" />
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-26127">
        <div data-id="element-26128">
          <div className="flex items-center gap-2" data-id="element-26129">
            <span className="text-xs text-slate-400 line-through" data-id="element-26130">
              18 000 ₽
            </span>
            <span className="text-[10px] font-bold text-emerald-600" data-id="element-26131">
              −{discountPercent}%
            </span>
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-26132">15 000 ₽</div>
        </div>
        <Button size="md" onClick={handleBuyProduct} className="shadow-md shadow-primary/20" data-id="element-26133">
          Записаться
        </Button>
      </div>
    </div>;
}